import React, { useEffect, useMemo, useState } from "react"

import { DollarSign, Loader2 } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useUpdateStopInstance } from "@/hooks/useCollections"
import { formatCost } from "@/lib/routeCostCalculation"
import { cn } from "@/lib/utils"
import { Stop, StopInstance } from "@/types/collections"

const savedRevenueOf = (stop: Stop): number | null => {
  const raw =
    stop.haulageDeliveryRevenueGbp ??
    (stop as unknown as { haulage_delivery_revenue_gbp?: number | null })
      .haulage_delivery_revenue_gbp
  return raw == null || raw === ("" as unknown) ? null : Number(raw)
}

const savedInjectionCostOf = (stop: Stop): number | null => {
  const raw =
    stop.directInjectionCostGbp ??
    (stop as unknown as { direct_injection_cost_gbp?: number | null }).direct_injection_cost_gbp
  return raw == null || raw === ("" as unknown) ? null : Number(raw)
}

/**
 * Saved behind a button, not on blur. Text input with decimal inputMode: number spinners nudge by a
 * penny. Editable after completion: haulage is priced once it has run.
 */
const StopMoneySection: React.FC<{
  stops: Stop[]
  column: "haulage_delivery_revenue_gbp" | "direct_injection_cost_gbp"
  heading: string
  fieldLabel: string
  noun: string
  savedValueOf: (stop: Stop) => number | null
}> = ({ stops, column, heading, fieldLabel, noun, savedValueOf }) => {
  const updateStopInstance = useUpdateStopInstance()

  const savedById = useMemo(() => {
    const map: Record<string, string> = {}
    for (const stop of stops) {
      const saved = savedValueOf(stop)
      map[stop.id] = saved != null ? String(saved) : ""
    }
    return map
  }, [stops, savedValueOf])

  const [draft, setDraft] = useState<Record<string, string>>(savedById)

  // Re-sync when the route reloads, so a save elsewhere isn't shadowed by a stale draft.
  useEffect(() => setDraft(savedById), [savedById])

  const parse = (raw: string): { value: number | null; valid: boolean } => {
    const trimmed = raw.trim()
    if (trimmed === "") return { value: null, valid: true }
    const parsed = Number(trimmed)
    return { value: parsed, valid: Number.isFinite(parsed) && parsed >= 0 }
  }

  const changed = stops.filter((stop) => (draft[stop.id] ?? "") !== (savedById[stop.id] ?? ""))
  const invalid = stops.filter((stop) => !parse(draft[stop.id] ?? "").valid)
  const total = stops.reduce((sum, stop) => sum + (parse(draft[stop.id] ?? "").value ?? 0), 0)

  const handleSave = async () => {
    if (!changed.length || invalid.length) return
    try {
      for (const stop of changed) {
        await updateStopInstance.mutateAsync({
          id: stop.id,
          data: { [column]: parse(draft[stop.id] ?? "").value } as Partial<StopInstance>,
        })
      }
      toast.success(`${heading} saved`, {
        description: `Updated ${changed.length} ${changed.length === 1 ? "stop" : "stops"}.`,
      })
    } catch (error) {
      console.error(`Error saving ${noun}:`, error)
      toast.error(`${heading} not saved`, { description: "Please try again." })
    }
  }

  return (
    <div className="rounded-lg border border-palette-jungleGreen200 bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="flex items-center gap-1.5 text-xs font-semibold text-palette-jungleGreen700">
          <DollarSign size={12} />
          {heading}
        </h4>
        <span className="text-xs tabular-nums text-palette-jungleGreen500">
          {formatCost(total)} total
        </span>
      </div>

      <div className="divide-y divide-palette-jungleGreen100">
        {stops.map((stop) => {
          const raw = draft[stop.id] ?? ""
          const { valid } = parse(raw)
          return (
            <div key={stop.id} className="flex items-center gap-2 py-1.5">
              <span
                className="min-w-0 flex-1 truncate text-xs text-palette-jungleGreen700"
                title={stop.name}
              >
                {stop.name}
              </span>
              {savedById[stop.id] === "" && (
                <span className="flex-none text-[10px] text-amber-600">not set</span>
              )}
              <label htmlFor={`${column}-${stop.id}`} className="sr-only">
                {fieldLabel} for {stop.name}
              </label>
              <div className="relative flex-none">
                <span className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[11px] text-palette-jungleGreen400">
                  £
                </span>
                <Input
                  id={`${column}-${stop.id}`}
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={raw}
                  disabled={updateStopInstance.isPending}
                  aria-invalid={!valid}
                  onChange={(e) => setDraft((prev) => ({ ...prev, [stop.id]: e.target.value }))}
                  className={cn(
                    "h-6 w-24 rounded border-palette-jungleGreen200 bg-white py-0 pl-4 pr-1.5 text-[11px] tabular-nums text-palette-jungleGreen900",
                    !valid && "border-red-400 text-red-700"
                  )}
                />
              </div>
            </div>
          )
        })}
      </div>

      <div className="mt-2 flex items-center justify-end gap-2">
        {invalid.length > 0 && (
          <span className="mr-auto text-[11px] text-red-600">Enter an amount of £0 or more.</span>
        )}
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!changed.length || invalid.length > 0 || updateStopInstance.isPending}
          className="h-7 gap-1.5 bg-amber-500 text-xs text-white hover:bg-amber-600"
        >
          {updateStopInstance.isPending && <Loader2 size={12} className="animate-spin" />}
          {updateStopInstance.isPending ? "Saving…" : `Save ${noun}`}
        </Button>
      </div>
    </div>
  )
}

export const HaulageRevenueSection: React.FC<{ stops: Stop[] }> = ({ stops }) => (
  <StopMoneySection
    stops={stops}
    column="haulage_delivery_revenue_gbp"
    heading="Haulage revenue"
    fieldLabel="Revenue"
    noun="revenue"
    savedValueOf={savedRevenueOf}
  />
)

export const DirectInjectionCostSection: React.FC<{ stops: Stop[] }> = ({ stops }) => (
  <StopMoneySection
    stops={stops}
    column="direct_injection_cost_gbp"
    heading="Direct injection cost"
    fieldLabel="Direct injection cost"
    noun="cost"
    savedValueOf={savedInjectionCostOf}
  />
)
